/* eslint-disable no-console */
import * as React from "react";
import {
  Container,
  Typography,
  Button,
  InputAdornment,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
  DialogActions,
  DialogContent,
  Checkbox,
  ListItem,
  ListItemIcon,
  Grid,
  ListItemText,
  Hidden,
  ButtonBase,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import BaseFilter from "../../core/models/BaseFilter";
import MaterialSelect from "../MaterialSelect";
import BuildIcon from "@material-ui/icons/Build";
import clsx from "clsx";
import { HomePageViewType } from "../../features/Home";
import SimpleTextField from "../SimpleTextField";
import SimpleDialog from "../SimpleDialog";
import { HomeTableColumns, HomeTableColumnsType } from "../HomeTable";
import FilterIcon from "../../assets/icons/FilterIcon";
import MaterialAutoComplete from "../MaterialAutoComplete";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing("12px ", 4),
      display: "flex",
      alignItems: "center",
      background: "#FFF",
      divShadow: "0px 2px 3px #0000000D",
      borderRadius: 1,
      [theme.breakpoints.only("sm")]: {
        padding: theme.spacing(1.25, 2),
      },

      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.5),
      },
    },

    categoryText: {
      fontSize: "1rem",
      fontWeight: "bold",
      color: theme.customPalette.text.darkgrey,
    },

    menuWrapper: {
      "display": "flex",
      "justifyContent": "center",
      "alignItems": "center",
      "paddingLeft": theme.spacing(1),
      "marginLeft": theme.spacing(1),

      "&.disable-border": {
        borderLeft: "unset",
      },

      [theme.breakpoints.down("sm")]: {
        borderLeft: "unset",
      },
    },

    mobileDialog: {
      "display": "flex",
      "justifyContent": "center",
      "alignItems": "center",
      "padding": theme.spacing(1),

      "& > *": {
        flexDirection: "column",
        rowGap: `${theme.spacing(3)}px`,
      },
    },

    button: {
      "display": "flex",
      "flex": 1,
      "alignItems": "center",
      "fontSize": "0.8rem",
      "color": theme.palette.text.secondary,

      "& svg": {
        fontSize: "1.5em",
        width: "1.5em",
        height: "1.5em",
      },
    },

    svg: {
      color: theme.customPalette.text.darkgrey,
      fontSize: "2.5rem",
    },

    svgLight: {
      color: "rgba(0,0,0,0.3)",
    },

    buildIconSvg: {
      paddingRight: theme.spacing(1),
      color: theme.palette.text.secondary,
    },

    selected: {
      color: theme.palette.primary.main,
    },

    dialog: {
      "padding": theme.spacing(2),
      "color": theme.customPalette.text.primary,

      "& h3": {
        fontWeight: "bold",
        margin: theme.spacing(0, 1),
      },

      "& .grid-root": {
        "color": theme.customPalette.text.primary,
        "fontWeight": "bold",

        "& >*:nth-child(6n + 1),>*:nth-child(6n + 2),>*:nth-child(6n + 3)": {
          backgroundColor: theme.customPaper.background.cardBackgroundLight,
        },

        "& >*:nth-child(6n + 4),>*:nth-child(6n + 5),>*:nth-child(6n + 6)": {
          backgroundColor: theme.customPaper.background.cardBackgroundLightContrast,
        },
      },
    },

    hidden: {
      display: "none",
    },
  })
);

export type HomePageFilterComponentProps = {
  handleSearchClick: () => void;
  handleClear: () => void;
  handleChangeSearch?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleClickExpandAll?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleClickCloseAll?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  filters: BaseFilter[];
  textFieldValue?: string;
  tableDialogOpen: boolean;
  handleCloseDialog: () => void;
  handleOpenDialog: () => void;
  columnsSelected: HomeTableColumnsType[];
  onClickColumnOption: (column: HomeTableColumnsType) => void;
  mobileDialogOpen: boolean;
  handleCloseMobileDialog: () => void;
  handleOpenMobileDialog: () => void;
};

function HomePageFilterComponent(props: HomePageFilterComponentProps) {
  const {
    handleChangeSearch,
    textFieldValue,
    filters,
    handleOpenDialog,
    mobileDialogOpen,
    handleOpenMobileDialog,
    handleCloseMobileDialog,
    handleSearchClick,
    handleClear,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down("xs"));

  const renderComponents = (filter: BaseFilter) => {
    const filterComponents: { [propertyName: string]: any } = {
      client: <MaterialAutoComplete key={filter.id} filter={filter} />,
      default: <MaterialSelect key={filter.id} filter={filter} />,
    };
    return filterComponents[filter.id] || filterComponents["default"];
  };

  return (
    <Container className={classes.root} maxWidth="xl">
      {/* Pesquisa por texto */}
      <div>
        <SimpleTextField
          value={textFieldValue}
          onChange={handleChangeSearch}
          label={!isMobileSize ? "Buscar Evento (Id)" : ""}
          placeholder={isMobileSize ? "Buscar Evento (Id)" : ""}
          id="filterSearch"
          variant="outlined"
          type="number"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Hidden smDown>
        {/* Filtros de card */}
        <div className={clsx(classes.menuWrapper)}>
          {filters.map((filter) => renderComponents(filter))}
        </div>

        {/* Customização da visão por table */}
        <div className={clsx(classes.menuWrapper, classes.hidden)}>
          <Button onClick={handleOpenDialog}>
            <BuildIcon className={classes.buildIconSvg} />
            <Typography className={classes.categoryText}>Opção de colunas</Typography>
          </Button>
        </div>
      </Hidden>

      <Hidden mdUp>
        <ButtonBase onClick={handleOpenMobileDialog}>
          <FilterIcon />
          <Button
            disableFocusRipple
            disableTouchRipple
            disableRipple
            variant="text"
            color="secondary"
            component="span"
          >
            Filtros
          </Button>
        </ButtonBase>
      </Hidden>

      <SimpleDialog
        draggable={false}
        id="mobile-home-filter"
        open={mobileDialogOpen}
        handleClose={handleCloseMobileDialog}
        maxWidth="xs"
        title="Filtrar"
      >
        <DialogContent className={classes.mobileDialog}>
          <div className={clsx(classes.menuWrapper)}>
            {filters.map((filter) => renderComponents(filter))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={(e) => {
              handleCloseMobileDialog();
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </SimpleDialog>
      <div>
        <Button
          style={{ marginRight: 5 }}
          variant="contained"
          onClick={(e) => {
            handleClear();
          }}
        >
          Limpar
        </Button>
        <Button
          variant="contained"
          onClick={(e) => {
            handleSearchClick();
          }}
        >
          Pesquisar
        </Button>
      </div>
    </Container>
  );
}

export default HomePageFilterComponent;
